import { createContext, useContext, useMemo } from "react";
import { setActiveMarket } from "./api";
import type { ReactNode } from "react";
import { useLocation, useParams } from "react-router-dom";
import type { MarketConfig } from "@shared/markets";

/**
 * 站台情境（市場 + 路徑組裝）
 * ---------------------------------------------------------------
 * 每一個站內連結都必須帶市場與語系前綴。與其在 200 個地方手寫
 * `/${market}/${locale}/jobs`，不如集中在這裡：用 usePath("/jobs")。
 *
 * 之後若要改網址結構（例如改成子網域 tw.colouredev.com），只需要改這個檔案。
 */

interface SiteValue {
  market: MarketConfig;
  locale: string;
  /** 把站內路徑補上市場與語系前綴 */
  path: (to: string) => string;
  /** 目前頁面（不含前綴）的路徑，用於語言切換時保持在同一頁 */
  currentPath: string;
}

const SiteContext = createContext<SiteValue | null>(null);

export function SiteProvider({ market, children }: { market: MarketConfig; children: ReactNode }) {
  const { locale = market.defaultLocale } = useParams();
  const location = useLocation();

  /*
    在 render 當下就寫入，不用 useEffect。

    useEffect 要等第一次 render 之後才跑，而路由元件的資料請求
    是在同一輪 render 裡就發出去的 —— 那一批請求會來不及帶到
    市場，於是切換市場後的第一次載入仍然是舊市場的資料。

    setActiveMarket 只是寫一個模組層級的變數，沒有副作用，
    在 render 中呼叫是安全的。
  */
  setActiveMarket(market.code);

  const value = useMemo<SiteValue>(() => {
    const prefix = `/${market.slug}/${locale}`;
    return {
      market,
      locale,
      path: (to: string) => {
        if (to.startsWith("http")) return to;
        const clean = to.startsWith("/") ? to : `/${to}`;
        return clean === "/" ? prefix : `${prefix}${clean}`;
      },
      currentPath: location.pathname.slice(prefix.length) || "/",
    };
  }, [market, locale, location.pathname]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteValue {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used inside <SiteProvider>");
  return ctx;
}

/** 常用簡寫：const path = usePath(); <Link to={path("/jobs")}> */
export function usePath() {
  return useSite().path;
}
